import React from 'react';
import { StyleSheet, View } from 'react-native';

import { BodyText, Collapsible, DisplayText, theme, useResponsive, VStack } from '@sg/garnish';

import { FlexLandingPageContent } from './FlexLandingPageContent';

export const FlexLandingPageFAQ = (props: FlexLandingPageFAQProps) => {
  const { faqTitle, faqDescription, faqEntries } = props;
  const { match } = useResponsive();

  return (
    <FlexLandingPageContent
      style={match([styles.containerXS, styles.containerXS, styles.containerLG])}
    >
      <View style={match([styles.headerXS, styles.headerXS, styles.headerSM])}>
        <DisplayText sizeMatch={['32', '40']} style={styles.title}>
          {faqTitle}
        </DisplayText>

        <BodyText sizeMatch={['16', '18']} style={styles.description}>
          {faqDescription}
        </BodyText>
      </View>

      <VStack style={match([null, null, styles.stackSM])} hasDivider gap={theme.spacing['12']}>
        {faqEntries.map((entry) => (
          <FlexLandingPageFAQEntry key={entry.id} entry={entry} />
        ))}
      </VStack>
    </FlexLandingPageContent>
  );
};

const FlexLandingPageFAQEntry = (props: FlexLandingPageFAQEntryProps) => {
  const { entry } = props;

  return (
    <Collapsible>
      <Collapsible.Summary.Container style={styles.summary}>
        <BodyText sizeMatch={['18']} style={[styles.title, styles.summaryText]}>
          {entry.title}
        </BodyText>

        <Collapsible.Icon
          iconName="IconCaretDown"
          iconColor={theme.colors.CHARCOAL}
          iconStrokeWidth={1.5}
          style={styles.caret}
        />
      </Collapsible.Summary.Container>

      <Collapsible.Details>
        <BodyText sizeMatch={['16']} style={[styles.description, styles.entryDescription]}>
          {entry.description}
        </BodyText>
      </Collapsible.Details>
    </Collapsible>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    paddingVertical: theme.spacing['10'],
    backgroundColor: theme.colors.CUCUMBER,
    gap: theme.spacing['10'],
  },
  containerLG: {
    paddingHorizontal: theme.spacing['24'],
    paddingVertical: theme.spacing['16'],
    flexDirection: 'row',
    gap: theme.spacing['20'],
    backgroundColor: theme.colors.CUCUMBER,
  },
  headerXS: {
    gap: theme.spacing['4'],
  },
  headerSM: {
    flex: 1,
    gap: theme.spacing['4'],
  },
  stackSM: {
    flex: 1,
  },
  title: {
    color: theme.colors.CHARCOAL,
  },
  description: {
    color: theme.colors.CHARCOAL,
  },
  summary: {
    flex: 1,
    gap: theme.spacing['6'],
    justifyContent: 'space-between',
  },
  summaryText: {
    flex: 1,
  },
  entryDescription: {
    marginTop: theme.spacing['2'],
  },
  caret: {
    alignSelf: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type FaqEntry = {
  id: string;
  title: string;
  description: string;
};

type FlexLandingPageFAQProps = {
  faqTitle: string;
  faqDescription: string;
  faqEntries: FaqEntry[];
};

type FlexLandingPageFAQEntryProps = {
  entry: FlexLandingPageFAQProps['faqEntries'][number];
};
